import { useState, useRef, useCallback } from "react";

// ─── THEME ───
const T = {
  bg: '#EDE8E1',
  sidebar: '#1a1730',
  sidebarHover: '#252240',
  sidebarActive: '#302d4a',
  card: '#FFFFFF',
  cardBorder: 'rgba(0,0,0,0.08)',
  text: '#1a1520',
  textSecondary: '#5c5466',
  textMuted: '#908892',
  accent: '#6366f1',
  accentLight: 'rgba(99,102,241,0.08)',
  accentText: '#5457e5',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
  shadowLg: '0 8px 30px rgba(0,0,0,0.08)',
  inputBg: '#F3EFE9',
  inputBorder: 'rgba(0,0,0,0.1)',
};

const STATUS_COLORS = {
  'Need Analysis': '#f59e0b',
  'Value Proposition': '#3b82f6',
  'Identify Decision': '#8b5cf6',
  'Proposal': '#06b6d4',
  'Review': '#10b981',
  'Closed Won': '#22c55e',
  'Closed Lost': '#ef4444',
};

const STAGES = ['Need Analysis', 'Value Proposition', 'Identify Decision', 'Proposal', 'Review'];

// ─── SVG ICONS ───
const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" };
  const map = {
    phone: <svg {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    mail: <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
    sms: <svg {...p}><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z"/></svg>,
    calendar: <svg {...p}><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
    clock: <svg {...p}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
    edit: <svg {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
    arrowRight: <svg {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>,
    check: <svg {...p}><path d="M20 6 9 17l-5-5"/></svg>,
    star: <svg {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill={color} stroke={color}/></svg>,
    file: <svg {...p}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>,
    note: <svg {...p}><path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    user: <svg {...p}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
    dollar: <svg {...p}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    target: <svg {...p}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    zap: <svg {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    link: <svg {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
    globe: <svg {...p}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    x: <svg {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
    chevDown: <svg {...p}><polyline points="6 9 12 15 18 9"/></svg>,
    chevRight: <svg {...p}><polyline points="9 18 15 12 9 6"/></svg>,
    copy: <svg {...p}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
    send: <svg {...p}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    play: <svg {...p}><polygon points="5 3 19 12 5 21 5 3" fill={color}/></svg>,
    mic: <svg {...p}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>,
    plus: <svg {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
    refresh: <svg {...p}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
    package: <svg {...p}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    hash: <svg {...p}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
    flag: <svg {...p}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
    eye: <svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    trendUp: <svg {...p}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
    shield: <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    alertCircle: <svg {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  };
  return map[name] || null;
};

// ─── SAMPLE DATA ───
const DEAL = {
  id: '6378177158461361',
  name: 'alice test',
  email: 'alicetest@gmail.com',
  phone: '01234567890',
  country: 'US',
  avatar: 'AT',
  assignedTo: { name: 'Domain Owner', initials: 'DO', color: '#6366f1' },
  source: 'Website Jeez',
  currentStage: 'Value Proposition',
  stageIndex: 1,
  amount: 260,
  probability: 50,
  product: 'Social Media',
  packageName: 'Best seller',
  packageCategory: 'Social Media',
  date: '20-Feb-2026',
  estPayment: '2026-02-26',
  concepts: 0,
  revisions: 1,
  leadId: 329,
  convertedId: '1771584613',
  description: 'Social media management for US market expansion.',
  features: [
    '5 weekly posting (any 4 social media platforms)',
    'Offer postings',
    'Updated cover photo (1 every month)',
    'Dedicated Social Media Expert',
    '100% Satisfaction guaranteed',
    'Promotional Postings',
    'Animated Gif Postings',
    '2 x 30sec animated videos per month',
    '24/7 support',
  ],
};

const DEALS_LIST = [
  { id: 1, name: 'alice test', package: 'Best seller', status: 'Need Analysis', amount: 260 },
  { id: 2, name: 'alice test', package: 'Starter pack', status: 'Proposal', amount: 150 },
  { id: 3, name: 'alice test', package: 'Enterprise', status: 'Closed Won', amount: 1200 },
];

const ACTIVITIES = [
  { id: 1, type: 'call', summary: 'Initial discovery call — discussed social media needs for US expansion', date: '2026-02-25 3:45 PM', agent: 'Domain Owner', duration: '12min', outcome: 'Interested', hasRecording: true },
  { id: 2, type: 'email', summary: 'Sent Best Seller package breakdown with case studies', date: '2026-02-24 11:20 AM', agent: 'Domain Owner', outcome: 'Opened' },
  { id: 3, type: 'note', summary: 'Client mentioned they tried social media in-house but couldn\'t maintain consistency. Key pain point: brand consistency across platforms.', date: '2026-02-24 11:35 AM', agent: 'Domain Owner' },
  { id: 4, type: 'call', summary: 'Follow-up — addressed pricing concerns, offered monthly billing option', date: '2026-02-23 2:10 PM', agent: 'Domain Owner', duration: '8min', outcome: 'Callback', hasRecording: true },
  { id: 5, type: 'sms', summary: 'Hi Alice, just following up on our conversation. Let me know if you have any questions!', date: '2026-02-22 4:30 PM', agent: 'Domain Owner' },
  { id: 6, type: 'stage', summary: 'Stage moved from Need Analysis → Value Proposition', date: '2026-02-22 2:00 PM', agent: 'System' },
];

const FOLLOW_UPS = [
  { id: 1, title: 'Call to close — discuss final pricing', date: '2026-02-27', time: '2:00 PM', priority: 'high', status: 'pending' },
  { id: 2, title: 'Send contract if agreed', date: '2026-02-28', time: '10:00 AM', priority: 'medium', status: 'pending' },
  { id: 3, title: 'Follow up on proposal review', date: '2026-02-25', time: '3:00 PM', priority: 'high', status: 'completed' },
];

const COMMENTS = [
  { id: 1, author: 'Domain Owner', text: 'Client is warm, just needs a push on timing. Mention the Q1 promo ending soon.', date: '2026-02-24 11:40 AM', pinned: true },
  { id: 2, author: 'Sarah (AM)', text: 'If this closes, I can do welcome call same week. Slot is open Thursday.', date: '2026-02-24 3:15 PM' },
];

// ─── HELPERS ───
const activityIcons = { call: 'phone', email: 'mail', sms: 'sms', note: 'note', meeting: 'calendar', stage: 'arrowRight', voicemail: 'mic' };
const activityColors = { call: '#3b82f6', email: '#8b5cf6', sms: '#06b6d4', note: '#f59e0b', meeting: '#10b981', stage: '#6366f1', voicemail: '#ef4444' };
const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

// ─── COMPONENTS ───

const Badge = ({ color, children, style = {} }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: color + '14', color, letterSpacing: '0.3px', ...style,
  }}>{children}</span>
);

const Card = ({ children, style = {}, hover = false, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12,
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transform: hovered ? 'translateY(-1px)' : 'none',
        transition: 'all 0.15s ease', cursor: onClick ? 'pointer' : 'default', ...style,
      }}
    >{children}</div>
  );
};

const ActionButton = ({ icon, label, color = T.accent, variant = 'outline', onClick, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  const filled = variant === 'filled';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
        borderRadius: 10, border: filled ? 'none' : `1px solid ${color}30`,
        background: filled ? (hovered ? color : color + 'ee') : (hovered ? color + '10' : color + '06'),
        color: filled ? '#fff' : color,
        cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        transition: 'all 0.15s', width: '100%', boxShadow: filled ? `0 2px 8px ${color}35` : 'none', ...style,
      }}
    >
      <Icon name={icon} size={15} color={filled ? '#fff' : color} /> {label}
    </button>
  );
};

const TabButton = ({ active, label, count, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px', borderRadius: 8, border: 'none',
        background: active ? T.accent : (hovered ? 'rgba(0,0,0,0.04)' : 'transparent'),
        color: active ? '#fff' : (hovered ? T.text : T.textSecondary),
        cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
        fontFamily: 'inherit', transition: 'all 0.15s',
        display: 'flex', alignItems: 'center', gap: 6,
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.06)',
          padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
          color: active ? '#fff' : T.textMuted,
        }}>{count}</span>
      )}
    </button>
  );
};

// ─── STAGE PROGRESS ───
const StageProgress = ({ stages, currentIndex, onStageClick }) => {
  const [hoveredStage, setHoveredStage] = useState(null);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, width: '100%' }}>
      {stages.map((stage, i) => {
        const isCompleted = i < currentIndex;
        const isCurrent = i === currentIndex;
        const isHovered = hoveredStage === i;
        const color = STATUS_COLORS[stage] || T.accent;
        return (
          <div key={stage} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', position: 'relative' }}
              onMouseEnter={() => setHoveredStage(i)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => onStageClick(i)}
            >
              {/* Node */}
              <div style={{
                width: isCurrent ? 36 : 28, height: isCurrent ? 36 : 28,
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isCompleted ? color : (isCurrent ? color : (isHovered ? color + '20' : '#e8e5e0')),
                border: isCurrent ? `3px solid ${color}40` : 'none',
                boxShadow: isCurrent ? `0 0 0 4px ${color}15, 0 2px 8px ${color}30` : (isHovered ? `0 0 0 3px ${color}15` : 'none'),
                transition: 'all 0.2s', transform: isHovered && !isCurrent ? 'scale(1.15)' : 'none',
              }}>
                {isCompleted ? (
                  <Icon name="check" size={14} color="#fff" />
                ) : isCurrent ? (
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#fff' }} />
                ) : (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: isHovered ? color : '#c4c0bb' }} />
                )}
              </div>
              {/* Label */}
              <span style={{
                fontSize: 11, fontWeight: isCurrent ? 700 : (isCompleted ? 600 : 500),
                color: isCurrent ? color : (isCompleted ? T.text : T.textMuted),
                textAlign: 'center', lineHeight: 1.2, whiteSpace: 'nowrap',
              }}>{stage}</span>
              {/* Click hint */}
              {isHovered && !isCurrent && (
                <span style={{
                  position: 'absolute', top: -28, background: T.text, color: '#fff',
                  padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                }}>Click to move here</span>
              )}
            </div>
            {/* Connector */}
            {i < stages.length - 1 && (
              <div style={{
                height: 3, flex: 1, borderRadius: 2, marginTop: -20,
                background: isCompleted ? color : '#e0ddd8',
                transition: 'all 0.3s',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── HEALTH GAUGE ───
const HealthGauge = ({ label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <span style={{ fontSize: 12, color: T.textSecondary, minWidth: 110 }}>{label}</span>
    <div style={{ flex: 1, height: 6, background: '#e8e5e0', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{
        width: `${value}%`, height: '100%', borderRadius: 3,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        transition: 'width 0.5s ease',
      }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 36, textAlign: 'right' }}>{value}%</span>
  </div>
);

// ─── MAIN APP ───
const CRMDetailPage = () => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [currentStageIndex, setCurrentStageIndex] = useState(DEAL.stageIndex);
  const [activeDeal, setActiveDeal] = useState(0);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logType, setLogType] = useState('call');
  const [expandedActivity, setExpandedActivity] = useState(null);

  const handleStageClick = (index) => {
    if (index !== currentStageIndex) {
      setCurrentStageIndex(index);
    }
  };

  const tabCounts = { timeline: ACTIVITIES.length, followups: FOLLOW_UPS.length, comments: COMMENTS.length, files: 3 };

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif", background: T.bg, color: T.text, overflow: 'hidden' }}>
      {/* ─── GOOGLE FONT ─── */}
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

     
      {/* <div style={{
        width: 260, background: T.sidebar, display: 'flex', flexDirection: 'column', flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.06)',
      }}>
        
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>Legendesk</div>
          <div style={{ fontSize: 11, color: '#818cf8', fontWeight: 600, letterSpacing: '1.5px', marginTop: 2 }}>CRM</div>
        </div>

      
        <div style={{ padding: '12px 10px', flex: 1, overflow: 'auto' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px', padding: '8px 10px 6px', marginBottom: 4 }}>NAVIGATION</div>
          {[
            { icon: 'target', label: 'Dashboard' },
            { icon: 'users', label: 'Pipeline', active: false },
            { icon: 'user', label: 'Leads', active: true },
            { icon: 'shield', label: 'Clients' },
            { icon: 'trendUp', label: 'Reports' },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 8, cursor: 'pointer', marginBottom: 2,
              background: item.active ? T.sidebarActive : 'transparent',
              color: item.active ? '#fff' : 'rgba(255,255,255,0.55)',
              transition: 'all 0.15s',
            }}>
              <Icon name={item.icon} size={16} color={item.active ? '#818cf8' : 'rgba(255,255,255,0.4)'} />
              <span style={{ fontSize: 13, fontWeight: item.active ? 600 : 400 }}>{item.label}</span>
            </div>
          ))}
        </div>

       
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '12px 10px 16px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.3)', letterSpacing: '1.5px', padding: '4px 10px 8px' }}>DEALS ({DEALS_LIST.length})</div>
          {DEALS_LIST.map((d, i) => (
            <div
              key={d.id}
              onClick={() => setActiveDeal(i)}
              style={{
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                background: activeDeal === i ? 'rgba(99,102,241,0.12)' : 'transparent',
                border: activeDeal === i ? '1px solid rgba(99,102,241,0.25)' : '1px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: activeDeal === i ? '#fff' : 'rgba(255,255,255,0.7)' }}>{d.package}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#818cf8' }}>${d.amount}</span>
              </div>
              <div style={{ marginTop: 4 }}>
                <Badge color={STATUS_COLORS[d.status] || '#6b7280'} style={{ fontSize: 10 }}>{d.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {/* ═══════════════════ MAIN CONTENT ═══════════════════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>

        {/* ─── BREADCRUMB BAR ─── */}
        {/* <div style={{
          padding: '10px 24px', background: T.card, borderBottom: `1px solid ${T.cardBorder}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMuted }}>
            <span style={{ cursor: 'pointer', color: T.accentText }}>Leads</span>
            <Icon name="chevRight" size={12} />
            <span style={{ cursor: 'pointer', color: T.accentText }}>Lead / Account Details</span>
            <Icon name="chevRight" size={12} />
            <span style={{ fontWeight: 600, color: T.text }}>{DEAL.name} — Deal Details</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px',
              background: T.inputBg, borderRadius: 8, border: `1px solid ${T.inputBorder}`,
            }}>
              <Icon name="search" size={14} color={T.textMuted} />
              <span style={{ fontSize: 12, color: T.textMuted }}>Search...</span>
            </div>
          </div>
        </div> */}

        {/* ─── SCROLLABLE CONTENT ─── */}
        <div style={{ flex: 1, overflow: 'auto',padding:"20px 0px", display: 'flex', gap: 20 }}>

          {/* ════════ CENTER COLUMN ════════ */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* ── HERO HEADER ── */}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              {/* Contact bar */}
              <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `linear-gradient(135deg, ${T.accent}, #8b5cf6)`, color: '#fff',
                  fontSize: 18, fontWeight: 700, flexShrink: 0,
                }}>{DEAL.avatar}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{DEAL.name}</span>
                    <Badge color={STATUS_COLORS[STAGES[currentStageIndex]]}>{STAGES[currentStageIndex]}</Badge>
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                    {[
                      { icon: 'phone', text: DEAL.phone, action: true },
                      { icon: 'mail', text: DEAL.email, action: true },
                      { icon: 'globe', text: DEAL.country },
                      { icon: 'link', text: DEAL.source },
                    ].map((item, i) => (
                      <span key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                        color: item.action ? T.accentText : T.textMuted,
                        cursor: item.action ? 'pointer' : 'default',
                      }}>
                        <Icon name={item.icon} size={12} color={item.action ? T.accentText : T.textMuted} />
                        {item.text}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>Assigned</span>
                  <div style={{
                    width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: DEAL.assignedTo.color + '18', color: DEAL.assignedTo.color,
                    fontSize: 12, fontWeight: 700, border: `2px solid ${DEAL.assignedTo.color}30`,
                  }}>{DEAL.assignedTo.initials}</div>
                </div>
              </div>

              {/* Stage Progress */}
              <div style={{ padding: '16px 24px 20px', background: 'rgba(0,0,0,0.015)', borderTop: `1px solid ${T.cardBorder}` }}>
                <StageProgress stages={STAGES} currentIndex={currentStageIndex} onStageClick={handleStageClick} />
              </div>
            </Card>

            {/* ── STAT CARDS ROW ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { label: 'Deal Amount', value: `$${DEAL.amount}`, icon: 'dollar', color: '#22c55e' },
                { label: 'Win Probability', value: `${DEAL.probability}%`, icon: 'target', color: '#f59e0b' },
                { label: 'Est. Payment', value: DEAL.estPayment, icon: 'calendar', color: '#3b82f6' },
                { label: 'Pending Deals', value: '1', icon: 'package', color: '#8b5cf6' },
              ].map((stat, i) => (
                <Card key={i} style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 500, marginBottom: 4 }}>{stat.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                    </div>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: stat.color + '10',
                    }}>
                      <Icon name={stat.icon} size={18} color={stat.color} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* ── TABS ── */}
            <Card style={{ padding: '6px 8px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { id: 'timeline', label: 'Timeline', count: tabCounts.timeline },
                { id: 'deal', label: 'Deal Info' },
                { id: 'followups', label: 'Follow Ups', count: tabCounts.followups },
                { id: 'comments', label: 'Comments', count: tabCounts.comments },
                { id: 'files', label: 'Files', count: tabCounts.files },
                { id: 'package', label: 'Package' },
              ].map(tab => (
                <TabButton key={tab.id} active={activeTab === tab.id} label={tab.label} count={tab.count} onClick={() => setActiveTab(tab.id)} />
              ))}
            </Card>

            {/* ── TAB CONTENT ── */}

            {/* TIMELINE TAB */}
            {activeTab === 'timeline' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
                {/* Quick log bar */}
                <Card style={{ padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>Quick log:</span>
                  {['call', 'email', 'sms', 'note', 'meeting'].map(type => (
                    <button key={type} onClick={() => { setLogType(type); setShowLogModal(true); }} style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                      borderRadius: 20, border: `1px solid ${activityColors[type]}25`,
                      background: activityColors[type] + '08', color: activityColors[type],
                      cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                    }}>
                      <Icon name={activityIcons[type]} size={12} color={activityColors[type]} />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </Card>

                {/* Timeline items */}
                <div style={{ position: 'relative', paddingLeft: 28 }}>
                  {/* Vertical line */}
                  <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: '#ddd8d2', borderRadius: 1 }} />

                  {ACTIVITIES.map((a, i) => {
                    const color = activityColors[a.type] || '#6b7280';
                    const expanded = expandedActivity === a.id;
                    return (
                      <div key={a.id} style={{ position: 'relative', marginBottom: 8 }}>
                        {/* Dot */}
                        <div style={{
                          position: 'absolute', left: -22, top: 16, width: 12, height: 12,
                          borderRadius: '50%', background: color + '20', border: `2.5px solid ${color}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                        }}>
                          <div style={{ width: 4, height: 4, borderRadius: '50%', background: color }} />
                        </div>

                        <Card hover onClick={() => setExpandedActivity(expanded ? null : a.id)} style={{ padding: '14px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: color + '12', flexShrink: 0,
                            }}>
                              <Icon name={activityIcons[a.type]} size={14} color={color} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{a.summary}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                  {a.outcome && <Badge color={color}>{a.outcome}</Badge>}
                                  {a.hasRecording && (
                                    <div style={{
                                      display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
                                      borderRadius: 6, background: '#ef444412', cursor: 'pointer',
                                    }}>
                                      <Icon name="play" size={10} color="#ef4444" />
                                      <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>REC</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 11, color: T.textMuted }}>
                                <span>{a.date}</span>
                                <span>by {a.agent}</span>
                                {a.duration && <span>{a.duration}</span>}
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* DEAL INFO TAB */}
            {activeTab === 'deal' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Card style={{ padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>Deal Information</span>
                    <button style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6,
                      border: `1px solid ${T.accent}25`, background: T.accentLight, color: T.accentText,
                      cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                    }}>
                      <Icon name="edit" size={12} color={T.accentText} /> Edit
                    </button>
                  </div>
                  {[
                    { label: 'Name', value: DEAL.name },
                    { label: 'Package', value: DEAL.packageName },
                    { label: 'Current Status', value: STAGES[currentStageIndex], badge: true, color: STATUS_COLORS[STAGES[currentStageIndex]] },
                    { label: 'Amount', value: `$${DEAL.amount}`, highlight: true },
                    { label: 'Product', value: DEAL.product },
                    { label: 'Date Created', value: DEAL.date },
                    { label: 'Deal Key', value: DEAL.id, mono: true },
                  ].map((field, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 6 ? `1px solid ${T.cardBorder}` : 'none' }}>
                      <span style={{ fontSize: 12, color: T.textMuted }}>{field.label}</span>
                      {field.badge ? <Badge color={field.color}>{field.value}</Badge>
                        : <span style={{ fontSize: 13, fontWeight: field.highlight ? 700 : 500, color: field.highlight ? '#22c55e' : T.text, fontFamily: field.mono ? 'monospace' : 'inherit', fontSize: field.mono ? 11 : 13 }}>{field.value}</span>}
                    </div>
                  ))}
                </Card>

                <Card style={{ padding: 20 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, display: 'block' }}>Contact & Source</span>
                  {[
                    { label: 'Contact Name', value: DEAL.assignedTo.name },
                    { label: 'Source', value: DEAL.source },
                    { label: 'Phone', value: DEAL.phone, action: true },
                    { label: 'Email', value: DEAL.email, action: true },
                    { label: 'Probability', value: `${DEAL.probability}%` },
                    { label: 'Lead ID', value: DEAL.leadId, mono: true },
                    { label: 'Converted ID', value: DEAL.convertedId, mono: true },
                  ].map((field, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < 6 ? `1px solid ${T.cardBorder}` : 'none' }}>
                      <span style={{ fontSize: 12, color: T.textMuted }}>{field.label}</span>
                      <span style={{
                        fontSize: field.mono ? 11 : 13, fontWeight: 500, fontFamily: field.mono ? 'monospace' : 'inherit',
                        color: field.action ? T.accentText : T.text, cursor: field.action ? 'pointer' : 'default',
                      }}>{field.value}</span>
                    </div>
                  ))}
                </Card>
              </div>
            )}

            {/* FOLLOW UPS TAB */}
            {activeTab === 'followups' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Card style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Scheduled Follow-Ups</span>
                  <ActionButton icon="plus" label="New Follow-Up" variant="filled" style={{ width: 'auto', padding: '7px 14px', fontSize: 12 }} />
                </Card>
                {FOLLOW_UPS.map(f => (
                  <Card key={f.id} hover style={{ padding: '14px 18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: f.status === 'completed' ? '#22c55e' : priorityColors[f.priority],
                        boxShadow: f.status === 'completed' ? 'none' : `0 0 0 3px ${priorityColors[f.priority]}20`,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, textDecoration: f.status === 'completed' ? 'line-through' : 'none', opacity: f.status === 'completed' ? 0.6 : 1 }}>{f.title}</div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: T.textMuted }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="calendar" size={11} color={T.textMuted} /> {f.date}</span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="clock" size={11} color={T.textMuted} /> {f.time}</span>
                        </div>
                      </div>
                      <Badge color={priorityColors[f.priority]}>{f.priority}</Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* COMMENTS TAB */}
            {activeTab === 'comments' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Card style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input placeholder="Add a comment or note for the team..." style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.inputBorder}`,
                      background: T.inputBg, fontSize: 13, fontFamily: 'inherit', color: T.text, outline: 'none',
                    }} />
                    <ActionButton icon="send" label="Post" variant="filled" style={{ width: 'auto', padding: '8px 16px' }} />
                  </div>
                </Card>
                {COMMENTS.map(c => (
                  <Card key={c.id} style={{ padding: '14px 18px', borderLeft: c.pinned ? '3px solid #f59e0b' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: 8, background: T.accent + '15', color: T.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{c.author.split(' ').map(w => w[0]).join('')}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{c.author}</span>
                          {c.pinned && <Badge color="#f59e0b" style={{ fontSize: 9, padding: '1px 6px' }}>Pinned</Badge>}
                          <span style={{ fontSize: 11, color: T.textMuted }}>{c.date}</span>
                        </div>
                        <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4, lineHeight: 1.5 }}>{c.text}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* FILES TAB */}
            {activeTab === 'files' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Card style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: `2px dashed ${T.accent}30` }}>
                  <Icon name="plus" size={24} color={T.accent} />
                  <span style={{ fontSize: 13, color: T.accentText, fontWeight: 600 }}>Drop files here or click to upload</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>Contracts, proposals, brand assets</span>
                </Card>
                {[
                  { name: 'BestSeller_Proposal.pdf', size: '245 KB', date: '2026-02-24', type: 'pdf' },
                  { name: 'SocialMedia_Brief.docx', size: '128 KB', date: '2026-02-22', type: 'doc' },
                  { name: 'Brand_Guidelines.pdf', size: '1.2 MB', date: '2026-02-20', type: 'pdf' },
                ].map((f, i) => (
                  <Card key={i} hover style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: f.type === 'pdf' ? '#ef444412' : '#3b82f612', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon name="file" size={16} color={f.type === 'pdf' ? '#ef4444' : '#3b82f6'} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: T.textMuted }}>{f.size} · {f.date}</div>
                    </div>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icon name="eye" size={16} color={T.textMuted} /></button>
                  </Card>
                ))}
              </div>
            )}

            {/* PACKAGE TAB */}
            {activeTab === 'package' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Card style={{ padding: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{DEAL.packageName}</div>
                      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{DEAL.packageCategory} · {DEAL.concepts} concepts · {DEAL.revisions} revision</div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>${DEAL.amount}</div>
                  </div>
                  <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 16, lineHeight: 1.5 }}>{DEAL.description}</div>

                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 10 }}>What's Included</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {DEAL.features.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', background: '#22c55e08', borderRadius: 8, border: '1px solid #22c55e15' }}>
                        <div style={{ marginTop: 2 }}><Icon name="check" size={13} color="#22c55e" /></div>
                        <span style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card style={{ padding: 20 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, display: 'block' }}>Additional Details</span>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Package Category', value: DEAL.packageCategory },
                      { label: 'Deal Stage', value: 'Proposal' },
                      { label: 'Number of Revisions', value: DEAL.revisions },
                      { label: 'Deal Amount', value: `$${DEAL.amount}` },
                      { label: 'Deal Probability', value: `${DEAL.probability}%` },
                      { label: 'Lead ID', value: DEAL.leadId },
                    ].map((item, i) => (
                      <div key={i} style={{ padding: '10px 14px', background: T.inputBg, borderRadius: 8 }}>
                        <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
          </div>

          {/* ════════ RIGHT SIDEBAR ════════ */}
          <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* QUICK ACTIONS */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '1px', marginBottom: 12, textTransform: 'uppercase' }}>Quick Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <ActionButton icon="phone" label="Log a Call" color="#3b82f6" variant="filled" onClick={() => { setLogType('call'); setShowLogModal(true); }} />
                <ActionButton icon="mail" label="Send Email" color="#8b5cf6" />
                <ActionButton icon="sms" label="Send SMS" color="#06b6d4" />
                <ActionButton icon="calendar" label="Schedule Follow Up" color="#f59e0b" />
                <ActionButton icon="arrowRight" label="Move to Next Stage" color="#22c55e" onClick={() => currentStageIndex < STAGES.length - 1 && setCurrentStageIndex(i => i + 1)} />
              </div>
            </Card>

            {/* DEAL HEALTH */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '1px', marginBottom: 14, textTransform: 'uppercase' }}>Deal Health</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <HealthGauge label="Win Probability" value={50} color="#3b82f6" />
                <HealthGauge label="Stage Progress" value={40} color="#f59e0b" />
                <HealthGauge label="Engagement" value={65} color="#22c55e" />
              </div>
              <div style={{ marginTop: 14, padding: '10px 12px', background: '#f59e0b0a', border: '1px solid #f59e0b20', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <Icon name="alertCircle" size={13} color="#f59e0b" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>Insight</span>
                </div>
                <span style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.4 }}>
                  Engagement is good but win probability is low. Consider a follow-up call to address objections.
                </span>
              </div>
            </Card>

            {/* UPCOMING */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '1px', marginBottom: 12, textTransform: 'uppercase' }}>Next Up</div>
              {FOLLOW_UPS.filter(f => f.status === 'pending').slice(0, 2).map(f => (
                <div key={f.id} style={{ padding: '10px 0', borderBottom: `1px solid ${T.cardBorder}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColors[f.priority], boxShadow: `0 0 0 3px ${priorityColors[f.priority]}20` }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{f.title}</div>
                    <div style={{ fontSize: 11, color: T.textMuted }}>{f.date} · {f.time}</div>
                  </div>
                </div>
              ))}
            </Card>

            {/* RECENT ACTIVITY SNAPSHOT */}
            <Card style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '1px', marginBottom: 12, textTransform: 'uppercase' }}>Activity Snapshot</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Calls', value: '3', color: '#3b82f6' },
                  { label: 'Emails', value: '1', color: '#8b5cf6' },
                  { label: 'SMS', value: '1', color: '#06b6d4' },
                  { label: 'Notes', value: '1', color: '#f59e0b' },
                ].map((s, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: s.color + '08', borderRadius: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* ═══════════════════ LOG ACTIVITY MODAL ═══════════════════ */}
      {showLogModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          backdropFilter: 'blur(4px)',
        }} onClick={() => setShowLogModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: T.card, borderRadius: 16, width: 480, maxHeight: '80vh', overflow: 'auto',
            boxShadow: T.shadowLg, border: `1px solid ${T.cardBorder}`,
          }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 16, fontWeight: 700 }}>Log Activity</span>
              <button onClick={() => setShowLogModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Icon name="x" size={18} color={T.textMuted} /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Type selector */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>Type</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['call', 'email', 'sms', 'meeting', 'note', 'voicemail'].map(t => (
                    <button key={t} onClick={() => setLogType(t)} style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                      borderRadius: 20, border: logType === t ? `1.5px solid ${activityColors[t]}` : `1px solid ${T.inputBorder}`,
                      background: logType === t ? activityColors[t] + '12' : T.card,
                      color: logType === t ? activityColors[t] : T.textMuted,
                      cursor: 'pointer', fontSize: 12, fontWeight: logType === t ? 600 : 400, fontFamily: 'inherit',
                    }}>
                      <Icon name={activityIcons[t]} size={12} color={logType === t ? activityColors[t] : T.textMuted} />
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Outcome */}
              {(logType === 'call') && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>Outcome</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {['Interested', 'Callback', 'Not Interested', 'No Answer', 'Voicemail'].map(o => (
                      <button key={o} style={{
                        padding: '5px 12px', borderRadius: 6, border: `1px solid ${T.inputBorder}`,
                        background: T.card, color: T.textSecondary, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit',
                      }}>{o}</button>
                    ))}
                  </div>
                </div>
              )}
              {/* Summary */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>Summary</label>
                <input placeholder="Brief description of the interaction" style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.inputBorder}`,
                  background: T.inputBg, fontSize: 13, fontFamily: 'inherit', color: T.text, outline: 'none', boxSizing: 'border-box',
                }} />
              </div>
              {/* Details */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 6, display: 'block' }}>Details</label>
                <textarea placeholder="Full notes, key points, action items..." style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.inputBorder}`,
                  background: T.inputBg, fontSize: 13, fontFamily: 'inherit', color: T.text, outline: 'none',
                  minHeight: 80, resize: 'vertical', boxSizing: 'border-box',
                }} />
              </div>
              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowLogModal(false)} style={{
                  padding: '9px 18px', borderRadius: 8, border: `1px solid ${T.inputBorder}`,
                  background: T.card, color: T.textSecondary, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
                }}>Cancel</button>
                <button onClick={() => setShowLogModal(false)} style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none',
                  background: `linear-gradient(135deg, ${T.accent}, #8b5cf6)`, color: '#fff',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  boxShadow: `0 2px 8px ${T.accent}35`,
                }}>Log Activity</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMDetailPage;